
import React, { useEffect, useMemo, useRef, useState, ChangeEvent, KeyboardEvent } from "react";
import { Link, useLocation } from "react-router-dom";
import {
    useReactTable,
    getCoreRowModel,
    createColumnHelper,
    Row,
} from "@tanstack/react-table";
import TablePaginationControls from "components/TableandmobilePaginationcontrol";
import { DesktopTableParts, MobileCardsOnly } from "components/TableandMobilecomponent";
import { showError } from "toast/toast";
import { ViewIconButton, EditIconButton, DeleteIconButton } from "components/DatatablesActionIcons";
import { getAuthCookie } from "../../utils/authCookies";
// ---------- Types (API rows) ----------
type VendorRequestRow = {
    id: string;
    vendor_name: string;
    category?: string;
    website?: string;
    email?: string;
    phone?: string;
    website_or_contact?: string;
    recommendation?: string;
    already_work_with?: boolean;
    multiple_offices?: boolean;
    status?: "Approved" | "Under Review" | "Pending" | "Rejected" | string;
    submitted_by?: string;
    date?: string; // ISO
};

// ---------- Helpers ----------
function fuzzyTextFilter<TData>(row: Row<TData>, columnId: string, filterValue: string) {
    const raw = row.getValue(columnId);
    const value = (typeof raw === "boolean" ? (raw ? "yes" : "no") : String(raw || ""))
        .toLowerCase();
    return value.includes((filterValue || "").toLowerCase());
}

const columnHelper = createColumnHelper<VendorRequestRow>();

const yesNoPill = (yes?: boolean) => (
    <span
        className={`badge rounded-pill px-3 py-2 ${yes ? "bg-success-subtle text-success-emphasis" : "bg-body-tertiary text-secondary-emphasis"
            }`}
    >
        {yes ? "Yes" : "No"}
    </span>
);

const statusPill = (s?: string) => {
    const v = (s || "").toLowerCase();
    const cls =
        v === "approved"
            ? "bg-success-subtle text-success-emphasis"
            : v === "pending"
                ? "bg-warning-subtle text-warning-emphasis"
                : v === "rejected"
                    ? "bg-danger-subtle text-danger-emphasis"
                    : "bg-primary-subtle text-primary-emphasis"; // Under Review / default
    return (
        <span className={`badge rounded-pill px-3 py-2 ${cls}`}>
            {s || "--"}
        </span>
    );
};

const fmtDate = (iso?: string) => (iso ? new Date(iso).toLocaleDateString() : "--");

const contactText = (r: VendorRequestRow) => r.website || r.website_or_contact || r.email || r.phone || "--";

// ============================================================
// Vendor Requests Table
// ============================================================
const SignageVendorRequests: React.FC = () => {
    const token = typeof window !== "undefined" ? getAuthCookie("sessionToken") : null;
    const location = useLocation();
    const isSignageTeammember = location.pathname.includes("/signageteammember");
    const baseRoute = isSignageTeammember ? "/signageteammember" : "/superadmin";

    const [rows, setRows] = useState<VendorRequestRow[]>([]);
    const [loading, setLoading] = useState(true);
    const [rowCount, setRowCount] = useState(0);

    const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 });
    const [globalFilter, setGlobalFilter] = useState("");
    const [searchInput, setSearchInput] = useState("");

    // optional: you can wire these to controls later
    const [statusFilter] = useState<string[]>(["Approved", "Under Review", "Pending", "Rejected"]);
    const [categoryFilter] = useState<string[]>([]);

    const topRef = useRef<HTMLDivElement>(null);

    // ---------- Fetch datatable ----------
    useEffect(() => {
        let ignore = false;

        async function load() {
            setLoading(true);
            try {
                const body = {
                    page: pagination.pageIndex + 1,
                    limit: pagination.pageSize,
                    //   status: statusFilter,
                    //   category: categoryFilter,
                    search: globalFilter,
                };

                const res = await fetch(`${process.env.REACT_APP_API_URL}/vendors/requests/datatable`, {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        ...(token ? { Authorization: `Bearer ${token}` } : {}),
                    },
                    body: JSON.stringify(body),
                });

                const json: any = await res.json();
                if (!res.ok || json?.error) {
                    const msg = json?.error || json?.message || "Failed to fetch vendor requests";
                    showError(msg);
                    throw new Error(msg);
                }

                if (!ignore) {
                    setRows(Array.isArray(json?.data) ? json.data : []);
                    setRowCount(Number(json?.total) || 0);
                }
            } catch (e) {
                if (!ignore) {
                    console.error(e);
                    setRows([]);
                    setRowCount(0);
                }
            } finally {
                if (!ignore) setLoading(false);
            }
        }

        load();
        return () => {
            ignore = true;
        };
    }, [token, pagination, globalFilter, statusFilter, categoryFilter]);

    // ---------- Columns (labels match your screenshot) ----------
    const columns = useMemo(
        () => [
            columnHelper.display({
                id: "serial",
                header: () => <div className="th-text S-No">S.No.</div>,
                cell: (info) => (
                    <div className="text-sm S-No">
                        {pagination.pageIndex * pagination.pageSize + info.row.index + 1}
                    </div>
                ),
            }),

            columnHelper.accessor("vendor_name", {
                id: "vendor_name",
                header: () => <div className="th-text S-No-2">Vendor Name</div>,
                cell: (info) => (
                    <div className="text-sm S-No-2 order-link-1">
                        <Link
                            to={`${baseRoute}/vendors/requests/${info.row.original.id}`}
                            className=" text-decoration-none order-link-1"
                        >
                            {info.getValue() || "--"}
                        </Link>
                    </div>
                ),
                filterFn: fuzzyTextFilter,
                enableGlobalFilter: true,
            }),

            columnHelper.accessor("category", {
                id: "category",
                header: () => <div className="th-text">Category/Type</div>,
                cell: (info) => <div className="text-sm">{info.getValue() || "--"}</div>,
                filterFn: fuzzyTextFilter,
                enableGlobalFilter: true,
            }),

            columnHelper.display({
                id: "website_or_contact",
                header: () => <div className="th-text">Website/Contact Info</div>,
                cell: (info) => <div className="text-sm text-truncate" style={{ maxWidth: 260 }}>
                    {contactText(info.row.original)}
                </div>,
                enableGlobalFilter: true,
            }),

            columnHelper.accessor("already_work_with", {
                id: "already",
                header: () => (
                    <div className="th-text">
                        Already Work With
                    </div>
                ),
                cell: (info) => <div className="text-sm">{yesNoPill(Boolean(info.getValue()))}</div>,
                enableGlobalFilter: false,
            }),

            columnHelper.accessor("multiple_offices", {
                id: "offices",
                header: () => (
                    <div className="th-text">
                        Multiple Offices
                    </div>
                ),
                cell: (info) => <div className="text-sm">{yesNoPill(Boolean(info.getValue()))}</div>,
                enableGlobalFilter: false,
            }),

            columnHelper.accessor("recommendation", {
                id: "why",
                header: () => <div className="th-text">Why Recommend</div>,
                cell: (info) => (
                    <div className="text-sm text-truncate" style={{ maxWidth: 260 }}>
                        {info.getValue() || "--"}
                    </div>
                ),
                filterFn: fuzzyTextFilter,
                enableGlobalFilter: true,
            }),

            columnHelper.accessor("status", {
                id: "status",
                header: () => <div className="th-text">Status</div>,
                cell: (info) => <div className="text-sm">{statusPill(info.getValue())}</div>,
                enableGlobalFilter: true,
            }),
            columnHelper.display({
                id: "actions",
                header: () => <div className="th-text">Actions</div>,
                cell: (info) => {
                    return (
                        <div className="d-flex align-items-center gap-2">
                            <ViewIconButton
                                to={`${baseRoute}/vendors/requests/${info.row.original.id}`} className="btn action-btn fs-2 text-center"
                                title="View"
                            />
                        </div>
                    );
                },
            }),
        ],
        [baseRoute, pagination]
    );

    // ---------- Table ----------
    const table = useReactTable({
        data: rows,
        columns,
        pageCount: Math.ceil(rowCount / pagination.pageSize),
        state: { pagination, globalFilter },
        manualPagination: true,
        manualFiltering: true,
        onPaginationChange: setPagination,
        onGlobalFilterChange: setGlobalFilter,
        getCoreRowModel: getCoreRowModel(),
    });

    // keep scroll at the top when page changes
    useEffect(() => {
        topRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, [pagination.pageIndex]);

    return (
        <div className="content" ref={topRef}>
            {/* Header with count/search — keep your existing UI; only handlers wired */}
            <div className="po-header mb-4 shadow-border-datatables">
                <div className="po-top d-flex justify-content-between align-items-start align-items-md-center flex-wrap gap-2">
                    <div className="po-title-wrap me-2 d-flex align-items-center">
                        <h4 className="po-title mb-0">Vendor requests</h4>
                    </div>

                    <div className="po-tools d-flex align-items-center flex-wrap gap-2 ms-auto">
                        <div className="input-group po-search">
                            <span className="input-group-text">
                                <i className="ri-search-line" />
                            </span>
                            <input
                                type="text"
                                className="form-control"
                                placeholder="Search by vendor name, category, contact..."
                                value={searchInput}
                                onChange={(e: ChangeEvent<HTMLInputElement>) => setSearchInput(e.target.value)}
                                onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => {
                                    if (e.key === "Enter") setGlobalFilter(searchInput);
                                }}
                            />
                        </div>
                    </div>
                </div>
            </div>

            {/* Table card (unchanged UI) */}
            <div className="card overflow-hidden production-card box-shadow-datatables shadow-border-datatables">
                <div className="align-items-center bg-white border-bottom d-flex justify-content-between px-4 py-4">
                    <h5 className="mb-0">Vendor Requests</h5>
                    <span className="badge rounded-pill bg-body-tertiary text-secondary-emphasis px-3 py-2">
                        {rowCount} requests
                    </span>
                </div>
                <hr className="mb-2" style={{ borderTop: "none" }} />

                {/* Desktop */}
                <div className="rdt-desktop">
                    <div className="production-scroll" style={{ "--rdt-min": "1200px" } as React.CSSProperties}>
                        <table className="table production-table align-middle mb-0 table-1">
                            <DesktopTableParts table={table} />
                        </table>
                    </div>
                </div>

                {/* Mobile */}
                <div className="rdt-mobile">
                    <MobileCardsOnly
                        table={table}
                        idColId="id"
                        titleColId="vendor_name"
                        actionColId="status"  // nothing to click; shows status on mobile card footer
                    />
                </div>
            </div>

            {table.getRowModel().rows.length === 0 ? (
                <div className="text-center my-4 text-muted" style={{ fontSize: "1.1rem" }}>
                    {loading ? "Loading..." : "No records found"}
                </div>
            ) : (
                <div className="mt-4 mb-4">
                    <TablePaginationControls table={table} />
                </div>
            )}
        </div>
    );
};

export default SignageVendorRequests;
